/**
 * ファイル選択・検証・送信中表示（data 属性駆動。05_ui.md §6）
 *
 * マークアップ規約:
 *   <form data-lms-file data-lms-file-accept=".zip" data-lms-file-max-bytes="62914560"
 *         data-lms-file-max-name-chars="50">
 *     <input type="file" data-lms-file-input>
 *     <span data-lms-file-name>未選択</span>
 *     <p data-lms-file-error hidden></p>
 *     <button type="submit" data-lms-file-submit disabled>提出する</button>
 *   </form>
 *
 * サーバー側検証を正とし、ここでの検証は無駄なアップロードを避けるための補助である
 * （02_api-and-screens.md §8）。
 *
 * 判定順序はサーバー側 SubmissionFileValidator と同一にする
 * （基底名 → 拡張子 → 文字数 → サイズ）。文言も一字一句同じにすること
 * （add-submission-filename-rules design.md D5）。
 *
 * 文言に埋め込む上限値（ファイル名の文字数・サイズの MB 表記）は、リテラルで直書きせず
 * data 属性から読んだ上限値から導出する（nameTooLongMessage / sizeOverMessage）。
 * サーバー側は SubmissionLimits.MAX_FILE_NAME_CHARS / MAX_FILE_SIZE_MB から同じ規則で
 * 組み立てており、上限を変えたときに片側だけ古い値を示す状態を作らない
 * （fix-submission-size-message design.md D1〜D3）。
 *
 * data 属性から読む 2 つの上限値（文字数・バイト数）は、いずれも resolveMaxNameChars /
 * resolveMaxBytes の正規化を必ず通す。属性の欠落・非数値・0 以下でも既定値へ落として
 * 検証を継続するため、上限値が不正であることを理由にクライアント側の事前検証が黙って
 * 無効化されることはない（fix-filename-limit-fallback design.md D1・D2）。
 */

const DEFAULT_ACCEPT = ".zip";
const DEFAULT_MAX_BYTES = 62_914_560;
const DEFAULT_MAX_NAME_CHARS = 50;

const MSG_EXTENSION = "zip ファイルのみ提出できます。";
const MSG_EMPTY = "空のファイルは提出できません。";
const MSG_NAME_REQUIRED = "ファイル名を入力してください。";

/**
 * 数値が「有限かつ正」ならその値を、そうでなければ既定値を返す純関数。
 *
 * data 属性由来の上限値（文字数・バイト数）の正規化と、文言組み立て時の多層防御とで
 * 同じ条件式が 3 箇所に現れるため、1 か所へ集約する（fix-filename-limit-fallback
 * design.md D2。fix-submission-size-message で「ガード条件 2 箇所重複」として
 * 見送られた Low 指摘の回収）。
 *
 * NaN・Infinity・0・負数がいずれも fallback へ落ちることがこの関数の契約である。
 * 条件を緩める（Number.isFinite を外す・>= 0 にする等）と呼び出し側 3 箇所の
 * ガードが同時に緩むので変更しないこと。
 */
function positiveNumberOr(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * ファイル名の文字数超過の文言を組み立てる（M-15）。
 *
 * 上限値をハードコードせず引数化することで、サーバー側 SubmissionFileValidator の
 * `"ファイル名は" + SubmissionLimits.MAX_FILE_NAME_CHARS + "文字以内にしてください。"`
 * と同じ組み立て方にする（実装時の是正: add-submission-filename-rules レビュー Medium-2）。
 * data 属性の値が変わっても文言と実際の上限がずれない。
 *
 * 不正値（NaN・Infinity・0 以下）が渡っても文言に NaN 等を出さないよう既定値へ落とす。
 * これは sizeOverMessage と同一の多層防御であり、通常経路では resolveMaxNameChars が
 * 先に正規化しているため到達しない。**冗長に見えてもこのガードを削ってはならない**
 * （CLAUDE.md の「正規化後の空判定は削ってはならない」と同じ方針。サイズ側と非対称に
 * 戻すと「なぜ片方だけガードがあるのか」を次に読む人が判断できなくなり、この関数が
 * 別経路から呼ばれるようになったときの保険も失われる。
 * fix-filename-limit-fallback design.md D3）。
 */
export function nameTooLongMessage(maxNameChars: number): string {
  const effective = positiveNumberOr(maxNameChars, DEFAULT_MAX_NAME_CHARS);
  return `ファイル名は${effective}文字以内にしてください。`;
}

/**
 * サイズ上限超過の文言を組み立てる（M-04）。
 *
 * 上限値をハードコードせず引数化することで、サーバー側 SubmissionFileValidator /
 * MultipartLimitExceptionHandler の
 * `"ファイルサイズが上限（" + SubmissionLimits.MAX_FILE_SIZE_MB + "MB）を超えています。"`
 * と同じ組み立て方にする（fix-submission-size-message design.md D1〜D3）。
 * data 属性の値が変わっても文言と実際の上限がずれない。
 *
 * MiB 基準の整数除算（切り捨て）で、サーバー側 MAX_FILE_SIZE_MB と同じ規則。
 * 不正値（NaN・0 以下）が渡っても文言に NaN を出さないよう既定値へ落とす（多層防御。
 * 通常経路では resolveMaxBytes が先に正規化している）。
 */
export function sizeOverMessage(maxBytes: number): string {
  const effective = positiveNumberOr(maxBytes, DEFAULT_MAX_BYTES);
  return `ファイルサイズが上限（${Math.floor(effective / (1024 * 1024))}MB）を超えています。`;
}

/**
 * data 属性からファイル名の文字数上限を読み取り、正規化する。
 *
 * 属性の欠落（undefined）だけでなく、数値として解釈できない値（"abc" 等）・0 以下・
 * 切り捨てて 0 以下になる小数も既定値へ落とす。従来は
 * `Number(dataset[...] ?? DEFAULT_MAX_NAME_CHARS)` だったため、属性に不正な文字列が
 * 入ると NaN になり、`Array.from(name).length > NaN` が常に false となって
 * クライアント側のファイル名文字数検証が黙って無効化されていた（確定仕様が求める
 * 「ファイル名の文字数の検証はクライアント側でも同一の規則で行う」層が消える）。
 * サイズ側の resolveMaxBytes と条件式・フォールバック方針を完全に一致させる
 * （fix-filename-limit-fallback design.md D1）。
 *
 * 文字数は個数なので Math.floor で整数へ丸める（文言に「50.5文字以内」と出さない。
 * サイズ側は sizeOverMessage の MB 換算で結果的に整数化しており、「文言に出す数値は
 * 整数」という同じ規則を、適用する場所を変えて当てているだけである）。
 * **丸めは正数判定より前に行うこと**: 後ろに置くと "0.5" が `> 0` を通過してから 0 へ
 * 丸められ、上限 0 文字（＝すべてのファイル名が違反）になってしまう。
 */
export function resolveMaxNameChars(raw: string | undefined): number {
  const parsed = Math.floor(Number(raw ?? DEFAULT_MAX_NAME_CHARS));
  return positiveNumberOr(parsed, DEFAULT_MAX_NAME_CHARS);
}

/**
 * data 属性から上限バイト数を読み取り、正規化する。
 *
 * 属性の欠落（undefined）だけでなく、数値として解釈できない値（"abc" 等）・0 以下も
 * 既定値へ落とす。従来は `Number(dataset[...] ?? DEFAULT_MAX_BYTES)` だったため、属性に
 * 不正な文字列が入ると NaN になり、`file.size > NaN` が常に false となって
 * クライアント側の事前検証が黙って無効化されていた（4 層同値管理の 1 層が消える）。
 * 文言に NaN が出ないようにするためでもある（fix-submission-size-message design.md D3 の注意）。
 */
export function resolveMaxBytes(raw: string | undefined): number {
  const parsed = Number(raw ?? DEFAULT_MAX_BYTES);
  return positiveNumberOr(parsed, DEFAULT_MAX_BYTES);
}

/**
 * 表示名（前後の空白を除いた名前）。サーバー側 SubmissionFileValidator#toDisplayName に対応する。
 *
 * ブラウザの File#name はパスを含まず制御文字も事実上入らないため、トリムのみを行う
 * （サーバー側はパス除去・制御文字除去も行う。サーバーが常に最終判断者であり、
 * クライアントで取りこぼしても多層防御としては問題ない）。
 */
function toDisplayName(rawName: string): string {
  return rawName.trim();
}

/**
 * 拡張子を除いた基底名が空でないことを検証する（M-16）。
 * 違反ならエラー文言を、問題なければ null を返す純関数。
 *
 * サーバー側 SubmissionFileValidator の (b) と同一規則。判定は「基底名が空でないこと」に限定し、
 * 「意味のある文字を含むこと」までは求めない（design.md D2。"..zip" は受理する）。
 */
export function checkBaseName(rawName: string, accept: string[]): string | null {
  const displayName = toDisplayName(rawName);
  const lower = displayName.toLowerCase();
  const ext = accept.find((candidate) => lower.endsWith(candidate));
  const base =
    ext === undefined ? displayName : displayName.slice(0, displayName.length - ext.length);
  return base.trim() === "" ? MSG_NAME_REQUIRED : null;
}

/**
 * ファイル名の文字数上限を検証する（M-15）。
 * 違反ならエラー文言を、問題なければ null を返す純関数。
 *
 * Array.from は文字列をコードポイント単位で反復するため、絵文字（サロゲートペア）1 個が
 * 1 文字として数えられる。サーバー側の String#codePointCount と一致する。
 * name.length（UTF-16 コードユニット数）を使ってはならない。
 */
export function checkNameLength(rawName: string, maxNameChars: number): string | null {
  const displayName = toDisplayName(rawName);
  return Array.from(displayName).length > maxNameChars
    ? nameTooLongMessage(maxNameChars)
    : null;
}

function formatSize(bytes: number): string {
  if (bytes < 1_048_576) {
    return `${Math.ceil(bytes / 1024)} KB`;
  }
  const mb = Math.floor((bytes / 1_048_576) * 10) / 10;
  return `${mb.toFixed(1)} MB`;
}

function showError(errorEl: HTMLElement | null, message: string): void {
  if (!errorEl) return;
  errorEl.textContent = message;
  errorEl.hidden = false;
}

function hideError(errorEl: HTMLElement | null): void {
  if (!errorEl) return;
  errorEl.textContent = "";
  errorEl.hidden = true;
}

export function initFileInput(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLFormElement>("[data-lms-file]").forEach((form) => {
    if (form.dataset["lmsFileReady"] === "1") return;
    form.dataset["lmsFileReady"] = "1";

    const input = form.querySelector<HTMLInputElement>("[data-lms-file-input]");
    const nameEl = form.querySelector<HTMLElement>("[data-lms-file-name]");
    const errorEl = form.querySelector<HTMLElement>("[data-lms-file-error]");
    const submitBtn = form.querySelector<HTMLButtonElement>("[data-lms-file-submit]");
    if (!input) return;

    const accept = (form.dataset["lmsFileAccept"] ?? DEFAULT_ACCEPT)
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0);
    const maxBytes = resolveMaxBytes(form.dataset["lmsFileMaxBytes"]);
    const maxNameChars = resolveMaxNameChars(form.dataset["lmsFileMaxNameChars"]);

    const handleChange = (): void => {
      const file = input.files?.[0] ?? null;
      if (!file) {
        if (nameEl) {
          nameEl.textContent = "未選択";
          nameEl.classList.add("is-empty");
        }
        hideError(errorEl);
        if (submitBtn) submitBtn.disabled = true;
        return;
      }

      // 判定順序はサーバー側と同一: 基底名 → 拡張子 → 文字数 → サイズ（→ 空はクライアント専用）
      const baseNameError = checkBaseName(file.name, accept);
      if (baseNameError) {
        showError(errorEl, baseNameError);
        if (submitBtn) submitBtn.disabled = true;
        return;
      }
      const lowerName = file.name.toLowerCase();
      if (!accept.some((ext) => lowerName.endsWith(ext))) {
        showError(errorEl, MSG_EXTENSION);
        if (submitBtn) submitBtn.disabled = true;
        return;
      }
      const nameLengthError = checkNameLength(file.name, maxNameChars);
      if (nameLengthError) {
        showError(errorEl, nameLengthError);
        if (submitBtn) submitBtn.disabled = true;
        return;
      }
      if (file.size > maxBytes) {
        showError(errorEl, sizeOverMessage(maxBytes));
        if (submitBtn) submitBtn.disabled = true;
        return;
      }
      if (file.size === 0) {
        showError(errorEl, MSG_EMPTY);
        if (submitBtn) submitBtn.disabled = true;
        return;
      }

      if (nameEl) {
        nameEl.textContent = `${file.name}（${formatSize(file.size)}）`;
        nameEl.classList.remove("is-empty");
      }
      hideError(errorEl);
      if (submitBtn) submitBtn.disabled = false;
    };

    const handleSubmit = (event: SubmitEvent): void => {
      if (form.dataset["lmsFileSending"] === "1") {
        event.preventDefault();
        return;
      }
      form.dataset["lmsFileSending"] = "1";
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "アップロード中…";
        submitBtn.setAttribute("aria-busy", "true");
      }
      window.lmsToast("アップロードを開始しました。しばらくお待ちください。", "info");
      // preventDefault しない: フォーム送信をそのまま続行させる
    };

    input.addEventListener("change", handleChange);
    form.addEventListener("submit", handleSubmit);
  });
}
